import React, { useState, useEffect } from 'react';
import ReactTooltip from 'react-tooltip';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { RootState } from 'store/reducers';
import {
  deleteContact,
  generateMultipleContactAccessDocument,
} from 'store/actions/contactActions';
import { setModal } from 'store/actions/modalActions';
import { temporaryFieldSaver } from 'store/actions/temporaryFieldSaverActions';
import {
  isAllowedToken,
  reactToast,
  scrollToElementId,
} from 'modules/common/helpers';

import { Button, Modal } from 'modules/common/components';
import { AnswersAccordion } from 'modules/estatePlan/components';
import { ContactCard, ContactOfCard } from 'modules/myContacts/components';
import { ContactForm } from 'modules/contact/components';
import { ProfileIncompleteProcess } from 'modules/dashboard/components';

import { PAGES } from 'modules/estatePlan/constants/enums';
import { NOTIFICATION_CODES as NC } from 'modules/notification/constants';
import { MODALS } from 'modules/common/constants';

import { ReactComponent as IconPlus } from 'images/icon-plus.svg';
import { ReactComponent as IconInfo } from 'images/icon-info.svg';
import { ReactComponent as IconEmpty } from 'images/icon_empty.svg';

function MyContacts() {
  const { t } = useTranslation(['common', 'mcontacts']);
  const location = useLocation();

  const previousRouteData: any = location.state || {};

  const currentUser: UserProfileDTO = useSelector(
    (state: RootState) => state.user.current
  );

  const selectContactOfData: ContactOfDTO[] =
    useSelector((state: RootState) => state.contactOf.list) || [];
  const contactsData =
    useSelector((state: RootState) => state.contacts.list) || [];
  const scrollToBeneId = useSelector(
    (state: RootState) => state.temporaryField.value
  );

  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [contactToEdit, setContactToEdit] = useState({} as ContactDTO);

  const [showProfileForm, setShowProfileForm] = React.useState(false);
  const contactsWithNoDocument = contactsData.filter(
    (contact) => !contact.documentId && !!contact.gender && !!contact.birthday
  );
  const [isDocumentsGenerating, setIsDocumentsGenerating] = useState(false);

  // opens contact form if user comes from notification
  useEffect(() => {
    if (previousRouteData?.notificationCode === NC.ADD_BENEFICIARIES) {
      handleShowForm();
      scrollToElementId('beneForm');
    }
  }, [previousRouteData]);

  // useRef does not work on re-rendering or re-assigning ref to an element on map method
  // easiest way is to use getElementById on a setTimeout
  useEffect(() => {
    if (scrollToBeneId && contactsData.length) {
      setTimeout(async () => {
        const selectBeneElement = document.getElementById(scrollToBeneId);
        await selectBeneElement?.scrollIntoView({ behavior: 'smooth' });
        dispatch(temporaryFieldSaver(null));
      }, 1);
    }
  }, [contactsData, scrollToBeneId]);

  useEffect(() => {
    ReactTooltip.rebuild();
  }, []);

  useEffect(() => {
    if (contactsWithNoDocument.length) {
      generateMissingDocuments(contactsWithNoDocument);
    } else {
      setIsDocumentsGenerating(false);
    }
  }, []);

  const generateMissingDocuments = (contactsWithoutDocument: ContactDTO[]) => {
    setIsDocumentsGenerating(true);
    if (!currentUser.passphraseOptOut && !isAllowedToken()) {
      reactToast.showWarning(
        t('mcontacts:contact_access_document_on_passphrase')
      );
      dispatch(
        setModal({
          name: MODALS.PASSPHRASE,
          props: {
            successAction: handlePassPhraseSuccess,
            cancelAction: handlePassPhraseCancel,
          },
        })
      );
    } else {
      reactToast.showWarning(t('mcontacts:contact_access_document_generating'));
      dispatch(generateMultipleContactAccessDocument(contactsWithoutDocument));
    }
  };

  const handleCloseAddForm = () => {
    setShowAddForm(false);
  };

  const handleShowForm = () => {
    if (!currentUser.profile) {
      setShowProfileForm(true);
      return undefined;
    }

    setShowAddForm(true);
  };

  const dispatch = useDispatch();

  const onEditContact = async (contactData: ContactDTO) => {
    setContactToEdit(contactData);
    handleCloseAddForm();
    setShowEditForm(true);
  };

  const handleCloseEditForm = () => {
    setShowEditForm(false);
  };

  const onDeleteContact = async (id: string, title: string) => {
    dispatch(
      setModal({
        name: MODALS.COMMON_MODAL,
        props: {
          title: title,
          description: t('mcontacts:delete_contact_confirm_text'),
          successAsyncAction: async () => {
            await dispatch(deleteContact(id));
          },
        },
      })
    );
  };

  const handleSuccessfulProfileUpdate = () => {
    setShowAddForm(true);
  };

  const handleHideProfileModal = () => {
    setShowProfileForm(false);
  };

  const handlePassPhraseSuccess = async () => {
    if (isDocumentsGenerating) {
      generateMissingDocuments(contactsWithNoDocument);
    }
  };

  const handlePassPhraseCancel = () => {
    setIsDocumentsGenerating(false);
  };

  return (
    <>
      {showProfileForm && (
        <Modal show onHide={handleHideProfileModal} hasCloseCross>
          <ProfileIncompleteProcess
            isProfilePage={false}
            onClose={handleHideProfileModal}
            onSuccess={handleSuccessfulProfileUpdate}
            profile={currentUser}
            profileFormTitle={t(
              `mcontacts:profile_not_complete_contact_form_title`
            )}
          />
        </Modal>
      )}

      {showEditForm && (
        <Modal show onHide={handleCloseEditForm}>
          <ContactForm
            onClose={handleCloseEditForm}
            contactItem={contactToEdit}
          />
        </Modal>
      )}

      <h1 className="Typography Typography--title u-weight-600 u-pb-10">
        {t('mcontacts:title')}
      </h1>

      <p className="t-mb-10">{t('mcontacts:description')}</p>

      <AnswersAccordion page={PAGES.MY_CONTACTS} />

      {currentUser.regular && (
        <>
          <div className="d-flex justify-content-between align-items-center u-pb-10">
            <h2 className="d-flex align-items-center Typography Typography--subTitle u-weight-600">
              <span className="t-mr-2.5">{t('mcontacts:contact_title')}</span>
              <IconInfo
                role="presentation"
                className="t-inline t-text-delta-700"
                data-tip={t('mcontacts:contact_tooltip')}
              />
            </h2>

            <Button category="secondary" onClick={handleShowForm}>
              <IconPlus
                role="presentation"
                className="t-inline t-text-alpha-600 t-mr-2"
              />
              <span className="xs:t-hidden sm:t-inline" id="beneForm">
                {t('common:add_new')}
              </span>
            </Button>
          </div>

          {showAddForm && (
            <>
              <div className="card u-pt-30 u-pr-30 u-pb-40 u-pl-30 u-bg-white">
                <ContactForm onClose={handleCloseAddForm} />
              </div>
              <div className="u-pb-10" />
            </>
          )}

          {contactsData &&
            contactsData.map((contactsListItem) => {
              return (
                <div key={contactsListItem.id} id={contactsListItem.id}>
                  <ContactCard
                    contactData={contactsListItem}
                    onDelete={onDeleteContact}
                    onEdit={onEditContact}
                    isViewAssetsAllowed
                    isMyContactsPage
                    isDocumentsGenerating={isDocumentsGenerating}
                  />
                </div>
              );
            })}

          {!contactsData.length && (
            <div>
              <div className="t-flex t-justify-center t-mt-4">
                <IconEmpty />
              </div>
              <p className="typo-epsilon t-text-beta-200 t-text-center t-mt-2.5">
                {t('mcontacts:empty_contact_text')}
              </p>
            </div>
          )}

          <div className="u-pb-40" />
        </>
      )}

      {selectContactOfData?.length > 0 && (
        <div className="t-mt-10">
          <h2 className="typo-beta t-mb-3">
            {t('mcontacts:contact_of_title')}
          </h2>
          {selectContactOfData?.map((contactOf) => (
            <ContactOfCard key={contactOf.id} contactOf={contactOf} />
          ))}
        </div>
      )}

      <div className="u-pb-40" />
    </>
  );
}

export default MyContacts;
